import { jsPDF } from "jspdf";

function resolveClientName(workorder) {
  return (
    workorder?.clientId?.naam ||
    workorder?.client?.naam ||
    workorder?.clientName ||
    workorder?.klant?.naam ||
    "-"
  );
}

function resolveProjectName(workorder) {
  return (
    workorder?.projectId?.naam ||
    workorder?.projectId?.title ||
    workorder?.project?.naam ||
    workorder?.project?.title ||
    workorder?.projectName ||
    workorder?.projectNummer ||
    "-"
  );
}

function buildContactRows(workorder) {
  return [
    { label: "Contactpersoon", value: workorder?.contact?.name || "-" },
    { label: "Functie", value: workorder?.contact?.role || "-" },
    { label: "Telefoon", value: workorder?.contact?.phone || "-" },
    { label: "E-mail", value: workorder?.contact?.email || "-" },
  ];
}

function toText(value, fallback = "-") {
  const raw = String(value ?? "").trim();
  return raw || fallback;
}

function toCurrency(value) {
  const num = Number(value || 0);
  if (!Number.isFinite(num)) return "0,00";
  return num.toLocaleString("nl-NL", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function toDate(value, fallback = "-") {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleDateString("nl-NL");
}

function toDateTime(value, fallback = "-") {
  if (!value) return fallback;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;
  return date.toLocaleString("nl-NL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function drawPageFooter(doc) {
  const width = doc.internal.pageSize.getWidth();
  const height = doc.internal.pageSize.getHeight();
  const page = doc.internal.getNumberOfPages();
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 120);
  doc.text(`Pagina ${page}`, width - 40, height - 20, { align: "right" });
}

function ensureSpace(doc, y, spaceNeeded) {
  const bottom = doc.internal.pageSize.getHeight() - 40;
  if (y + spaceNeeded <= bottom) return y;
  drawPageFooter(doc);
  doc.addPage();
  return 40;
}

function drawSectionTitle(doc, y, title) {
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(20, 20, 20);
  doc.text(title, 40, y);
  return y + 14;
}

function drawKeyValueRows(doc, y, rows) {
  const leftX = 40;
  const valueX = 150;
  const lineHeight = 14;
  doc.setFontSize(10);

  for (const row of rows) {
    y = ensureSpace(doc, y, 18);
    doc.setFont("helvetica", "bold");
    doc.text(`${row.label}:`, leftX, y);
    doc.setFont("helvetica", "normal");
    const split = doc.splitTextToSize(toText(row.value), 400);
    doc.text(split, valueX, y);
    y += Math.max(lineHeight, split.length * lineHeight);
  }

  return y + 4;
}

function drawParagraph(doc, y, text) {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  const split = doc.splitTextToSize(toText(text), 515);
  for (const line of split) {
    y = ensureSpace(doc, y, 14);
    doc.text(line, 40, y);
    y += 14;
  }
  return y + 2;
}

function drawBulletList(doc, y, items, emptyText) {
  if (!items.length) {
    return drawParagraph(doc, y, emptyText);
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  for (const item of items) {
    const line = `- ${toText(item)}`;
    const split = doc.splitTextToSize(line, 510);
    for (const part of split) {
      y = ensureSpace(doc, y, 14);
      doc.text(part, 40, y);
      y += 14;
    }
  }
  return y + 2;
}

export async function exportWorkorderPdf({
  workorder,
  financial,
  assignedUserLabel = "-",
  actions = [],
  latestPhotos = [],
  companyDetails = null,
  workorderPdfSnapshot = null,
}) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  let y = 40;

  const title = `Werkbon ${toText(workorder?.werkbonNummer || workorder?.nummer || workorder?._id, "")}`;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(17, 24, 39);
  doc.text(title || "Werkbon", 40, y);
  y += 20;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(80, 80, 80);
  doc.text(`Status: ${toText(workorder?.status)}`, 40, y);
  doc.text(`Gegenereerd: ${toDateTime(new Date())}`, 350, y);
  y += 20;

  y = drawSectionTitle(doc, y, "Kerngegevens");
  y = drawKeyValueRows(doc, y, [
    { label: "Klant", value: resolveClientName(workorder) },
    { label: "Project", value: resolveProjectName(workorder) },
    {
      label: "Locatie",
      value: workorder?.locationText || workorder?.opdracht?.locatie || workorder?.adres || "-",
    },
    ...buildContactRows(workorder),
    { label: "Monteur", value: assignedUserLabel || "-" },
    { label: "Geplande datum", value: toDate(workorder?.plannedDate) },
    { label: "Starttijd", value: toText(workorder?.plannedStartTime) },
  ]);

  const normalizedSnapshot = normalizeWorkorderPdfSnapshotForPdf(workorderPdfSnapshot);
  const normalizedCompany = normalizeCompanyDetailsForPdf(
    normalizedSnapshot.company || companyDetails
  );
  const normalizedTemplate = normalizeWorkorderDocumentTemplateForPdf(normalizedSnapshot.template);
  const normalizedBranding = normalizeBrandingForPdf(normalizedSnapshot.branding);

  if (normalizedTemplate.headerText) {
    y = ensureSpace(doc, y, 20);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    const headerLines = doc.splitTextToSize(normalizedTemplate.headerText, 515).slice(0, 2);
    doc.text(headerLines, 40, y);
    y += headerLines.length * 12 + 6;
  }

  if (hasCompanyDetailsForPdf(normalizedCompany) && normalizedTemplate.showCompanyDetails) {
    y = ensureSpace(doc, y, 24);
    y = drawSectionTitle(doc, y, "Uitvoerder (bedrijfsgegevens)");
    y = drawKeyValueRows(doc, y, [
      { label: "Bedrijf", value: normalizedCompany.tradeName || normalizedCompany.legalName || "-" },
      { label: "Juridische naam", value: normalizedCompany.legalName || "-" },
      { label: "KvK", value: normalizedCompany.kvkNumber || "-" },
      { label: "BTW", value: normalizedCompany.vatNumber || "-" },
      { label: "E-mail", value: normalizedCompany.email || "-" },
      { label: "Telefoon", value: normalizedCompany.phone || "-" },
      { label: "Website", value: normalizedCompany.website || "-" },
      { label: "IBAN", value: normalizedCompany.iban || "-" },
      { label: "BIC", value: normalizedCompany.bic || "-" },
      { label: "Adres", value: normalizedCompany.addressLabel || "-" },
    ]);
  }

  y = ensureSpace(doc, y, 24);
  y = drawSectionTitle(doc, y, "Werkopdracht");
  y = drawParagraph(
    doc,
    y,
    workorder?.omschrijving || workorder?.opdracht?.omschrijving || workorder?.scope?.samenvatting || "-"
  );

  y = ensureSpace(doc, y, 24);
  y = drawSectionTitle(doc, y, "Werkzaamheden");
  y = drawBulletList(
    doc,
    y,
    actions.map((item) => `${toText(item?.type)} • ${toText(item?.status)} • ${toText(item?.title)}`),
    "Geen werkzaamheden geregistreerd."
  );

  const hourEntries = Array.isArray(financial?.hourEntries) ? financial.hourEntries : [];
  if (normalizedTemplate.showHours) {
    y = ensureSpace(doc, y, 24);
    y = drawSectionTitle(doc, y, "Urenregistratie");
    y = drawBulletList(
      doc,
      y,
      hourEntries.map((u) => {
        const hours = Number(u?.hours || 0).toFixed(2);
        const hourType = toText(u?.hourTypeName, "uur");
        const date = toDate(u?.datum);
        const desc = toText(u?.description, "");
        return `${date} • ${hours} uur (${hourType})${desc ? ` • ${desc}` : ""}`;
      }),
      "Nog geen uren geregistreerd."
    );
  }

  const materialEntries = Array.isArray(financial?.materialEntries) ? financial.materialEntries : [];
  if (normalizedTemplate.showMaterials) {
    y = ensureSpace(doc, y, 24);
    y = drawSectionTitle(doc, y, "Materialen");
    y = drawBulletList(
      doc,
      y,
      materialEntries.map((m) => {
        const qty = Number(m?.qty || 0);
        const unit = toText(m?.unit, "st");
        const desc = toText(m?.description);
        const code = toText(m?.articleCode, "");
        return `${desc} • ${qty} ${unit}${code ? ` • Art.nr ${code}` : ""}`;
      }),
      "Nog geen materialen geregistreerd."
    );
  }

  if (normalizedTemplate.showPrices) {
    y = ensureSpace(doc, y, 24);
    y = drawSectionTitle(doc, y, "Financiele samenvatting");
    y = drawKeyValueRows(doc, y, [
      { label: "Uren excl.", value: `EUR ${toCurrency(financial?.financialTotals?.hoursSubtotal)}` },
      { label: "Materiaal excl.", value: `EUR ${toCurrency(financial?.financialTotals?.materialsSubtotal)}` },
      { label: "Subtotaal excl.", value: `EUR ${toCurrency(financial?.financialTotals?.subtotalExcl)}` },
      { label: "BTW", value: `EUR ${toCurrency(financial?.financialTotals?.vatTotal)}` },
      { label: "Totaal incl.", value: `EUR ${toCurrency(financial?.financialTotals?.totalIncl)}` },
    ]);
  }

  if (normalizedTemplate.showPhotos) {
    y = ensureSpace(doc, y, 24);
    y = drawSectionTitle(doc, y, "Laatste resultaatfotos");
    y = drawBulletList(
      doc,
      y,
      latestPhotos.map((photo, index) => {
        const date = toDateTime(photo?.createdAt, "");
        const category = toText(photo?.category, "Overig");
        const url = toText(photo?.url, "");
        return `Foto ${index + 1} • ${category}${date ? ` • ${date}` : ""}${url ? ` • ${url}` : ""}`;
      }),
      "Geen resultaatfotos beschikbaar."
    );
  }

  if (normalizedTemplate.footerText) {
    y = ensureSpace(doc, y, 18);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    const footerLines = doc.splitTextToSize(normalizedTemplate.footerText, 515).slice(0, 2);
    doc.text(footerLines, 40, y);
    y += footerLines.length * 11;
  }

  if (normalizedBranding.primaryColor) {
    const [r, g, b] = hexToRgb(normalizedBranding.primaryColor) || [17, 24, 39];
    doc.setDrawColor(r, g, b);
    doc.setLineWidth(1);
    doc.line(40, 28, 555, 28);
  }

  drawPageFooter(doc);
  const safeNumber = toText(workorder?.werkbonNummer || workorder?.nummer || "werkbon").replace(
    /[^a-zA-Z0-9_-]+/g,
    "_"
  );
  doc.save(`${safeNumber}.pdf`);
}

function normalizeCompanyDetailsForPdf(companyDetails) {
  const source = companyDetails && typeof companyDetails === "object" ? companyDetails : {};
  const address = source.address && typeof source.address === "object" ? source.address : {};
  const addressLine = [
    String(address.street || "").trim(),
    String(address.houseNumber || "").trim(),
  ]
    .filter(Boolean)
    .join(" ");
  const cityLine = [
    String(address.postalCode || "").trim(),
    String(address.city || "").trim(),
    String(address.country || "").trim().toUpperCase(),
  ]
    .filter(Boolean)
    .join(" ");
  return {
    legalName: String(source.legalName || "").trim(),
    tradeName: String(source.tradeName || "").trim(),
    kvkNumber: String(source.kvkNumber || "").trim(),
    vatNumber: String(source.vatNumber || "").trim(),
    email: String(source.email || "").trim(),
    phone: String(source.phone || "").trim(),
    website: String(source.website || "").trim(),
    iban: String(source.iban || "").trim().toUpperCase(),
    bic: String(source.bic || "").trim().toUpperCase(),
    addressLabel: [addressLine, cityLine].filter(Boolean).join(", "),
  };
}

function normalizeBrandingForPdf(branding) {
  const source = branding && typeof branding === "object" ? branding : {};
  const fallbackColor = "#0f172a";
  const fallbackSecondary = "#2563eb";
  return {
    logoUrl: String(source.logoUrl || "").trim(),
    primaryColor: normalizeHexColor(String(source.primaryColor || "").trim(), fallbackColor),
    secondaryColor: normalizeHexColor(String(source.secondaryColor || "").trim(), fallbackSecondary),
    fontFamily: String(source.fontFamily || "Inter").trim() || "Inter",
  };
}

function normalizeWorkorderDocumentTemplateForPdf(template) {
  const source = template && typeof template === "object" ? template : {};
  return {
    enabled: source.enabled !== false,
    headerText: String(source.headerText || "").trim(),
    footerText: String(source.footerText || "").trim(),
    documentTitle: String(source.documentTitle || "").trim(),
    showHours: source.showHours !== false,
    showMaterials: source.showMaterials !== false,
    showPhotos: source.showPhotos !== false,
    showPrices: source.showPrices === true,
    showSignature: source.showSignature !== false,
    showCustomerSignature: source.showCustomerSignature !== false,
    showTechnicianSignature: source.showTechnicianSignature !== false,
    showCompanyDetails: source.showCompanyDetails !== false,
    showQrCode: source.showQrCode === true,
    layoutPreset: String(source.layoutPreset || "standaard").trim() || "standaard",
  };
}

function normalizeWorkorderPdfSnapshotForPdf(snapshot) {
  const source = snapshot && typeof snapshot === "object" ? snapshot : {};
  return {
    generatedAt: source.generatedAt || null,
    exportedAt: source.exportedAt || null,
    company: source.company && typeof source.company === "object" ? source.company : null,
    branding: source.branding && typeof source.branding === "object" ? source.branding : {},
    template: source.template && typeof source.template === "object" ? source.template : {},
  };
}

function normalizeHexColor(value, fallback) {
  const raw = String(value || "").trim();
  if (/^#[0-9a-fA-F]{6}$/.test(raw)) return raw;
  return fallback;
}

function hexToRgb(hex) {
  const raw = normalizeHexColor(hex, "");
  if (!raw) return null;
  const value = raw.slice(1);
  return [
    Number.parseInt(value.slice(0, 2), 16),
    Number.parseInt(value.slice(2, 4), 16),
    Number.parseInt(value.slice(4, 6), 16),
  ];
}

function hasCompanyDetailsForPdf(companyDetails) {
  const values = [
    companyDetails?.legalName,
    companyDetails?.tradeName,
    companyDetails?.kvkNumber,
    companyDetails?.vatNumber,
    companyDetails?.email,
    companyDetails?.phone,
    companyDetails?.website,
    companyDetails?.iban,
    companyDetails?.bic,
    companyDetails?.addressLabel,
  ];
  return values.some((value) => String(value || "").trim().length > 0);
}
